import Layout from 'components/UI/Layout';
import BreadcrumbNav from 'components/UI/BreadcrumbNav';
import React, { ReactElement } from 'react';
import { BrandedSiteProps, PageDates } from 'lib/routing';
import {
  IFragmentInsuranceFields,
  IMetaLocationClosedDates,
  IPageDoctorFields,
  IPageLocationFields,
} from 'types/contentful';
import { doctorDisplayName } from 'lib/util';
import SharedLocationDataContext from 'context/SharedLocationDataContext';
import { Review, ReviewAverage, reviewToTestimoial } from 'lib/reviewinc';
import Testimonial from 'components/Contentful/Testimonials';
import { PreparedComponentObject, DynamicComponentTypes } from 'components';
import DoctorLocationsAccordian from 'components/UI/Doctor/DoctorLocationsAccordian';
import PhysicianSchema from 'components/StructuredData/PhysicianSchema';
import DoctorImageAndPageSummary from 'components/UI/Doctor/DoctorImageAndPageSummary';
import DoctorBiography from 'components/UI/Doctor/DoctorBiography';
import InsuranceAndPayment from 'components/UI/Doctor/InsuranceAndPayment';
import ProductCarousel from 'components/Contentful/ProductCarousel';
import SlimHero from 'components/Contentful/Hero/SlimHero';
import { Document } from '@contentful/rich-text-types';
import type { BreadcrumbItem } from 'lib/cms/breadcrumbs';

export type DoctorProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  pageType: 'doctor';
  preview: boolean;
  content: IPageDoctorFields;
  reviewAvg?: ReviewAverage;
  reviews?: Review[];
  officeInfo: IPageLocationFields | null;
  hideEyeOnPin: boolean;
  siteData: BrandedSiteProps;
  currentSlug: string;
  paymentsAccepted: string[];
  secondaryLocations: IPageLocationFields[] | [];
  closedDates?: IMetaLocationClosedDates;
  pageDates?: PageDates;
  scheduleApptCtaText: string;
  insuranceDisclaimer: string;
  insuranceSlug: string;
  markerIcon?: string;
  doctorVideo?: PreparedComponentObject<K, CP>[] | null;
  billingInquirySlug: string;
  insuranceProviders: IFragmentInsuranceFields[];
  breadcrumbs: BreadcrumbItem[];
};

export default function DoctorLayout({
  preview,
  siteData,
  content,
  reviewAvg,
  reviews,
  officeInfo,
  secondaryLocations,
  currentSlug,
  paymentsAccepted,
  closedDates,
  pageDates,
  scheduleApptCtaText,
  insuranceDisclaimer,
  insuranceSlug,
  billingInquirySlug,
  markerIcon = 'Eye-V01',
  doctorVideo,
  insuranceProviders,
  breadcrumbs,
}: DoctorProps): ReactElement {
  const fullNameAndTitles = doctorDisplayName(content);

  // Helper function to replace {siteName} placeholder in RichText Document
  const replaceSiteNameInDocument = (
    document: Document | undefined,
    siteName: string
  ): Document | undefined => {
    if (!document) return undefined;
    const stringValue = JSON.stringify(document);
    const sanitizedSiteName = siteName.replace(/[^\w]+|\s+/g, ' ');
    const hydrated = stringValue.replace(/{siteName}/gi, sanitizedSiteName);
    return JSON.parse(hydrated);
  };

  const nextSeoProps = {
    description: `${
      officeInfo?.city ? `${officeInfo?.city} based eye doctor` : 'Doctor'
    } ${fullNameAndTitles} diagnoses, treats, and prescribes for a variety of eye conditions. Schedule an exam at ${
      siteData.siteName
    }.`,
    title: `${fullNameAndTitles}${officeInfo?.city ? ' ' + officeInfo?.city : ''} ${
      content.typeOfDoctor
    }`,
  };

  // Determine if Insurance & Payment section should be shown anywhere on the page
  const shouldShowInsuranceAndPayment =
    ((insuranceProviders && insuranceProviders.length > 0) ||
      (paymentsAccepted && paymentsAccepted.length > 0)) &&
    (!siteData.doctorPagesComponentOrder ||
      siteData.doctorPagesComponentOrder.length === 0 ||
      siteData.doctorPagesComponentOrder.some(
        (comp) => comp.fields.name === 'Insurance and Payment'
      ));

  const displayAnchorLinks = (): ReactElement | undefined => {
    let shouldShowMoreLocations = secondaryLocations.length > 1;
    if (siteData.doctorPagesComponentOrder && siteData.doctorPagesComponentOrder.length > 0) {
      shouldShowMoreLocations = siteData.doctorPagesComponentOrder?.some(
        (comp) => comp.fields.name === 'Doctor Locations'
      );
    }

    let shouldShowAboutDoctor =
      content.about || content.education || content.honorsAwards || content.membershipsAffiliations
        ? true
        : false;
    if (siteData.doctorPagesComponentOrder && siteData.doctorPagesComponentOrder.length > 0) {
      shouldShowAboutDoctor = siteData.doctorPagesComponentOrder?.some(
        (comp) => comp.fields.name === 'Doctor Biography'
      );
    }

    const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string): void => {
      e.preventDefault();
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    return (
      <>
        {shouldShowMoreLocations && (
          <a
            href={`#my_locations`}
            className="inline-flex items-center justify-center px-6 py-button leading-tight rounded-full border border-2px border-teal text-teal-fontcolor font-heading hover:no-underline"
            onClick={(e) => handleSmoothScroll(e, 'my_locations')}
          >
            View 1 More Location
          </a>
        )}
        {shouldShowAboutDoctor && (
          <a
            href={`#about_doctor`}
            className="inline-flex items-center justify-center px-6 py-button leading-tight rounded-full border border-2px border-teal text-teal-fontcolor font-heading hover:no-underline"
            onClick={(e) => handleSmoothScroll(e, 'about_doctor')}
          >
            About {content.firstName} {content.lastName}
          </a>
        )}
        {shouldShowInsuranceAndPayment && (
          <a
            href={`#insurance_and_payments`}
            className="inline-flex items-center justify-center px-6 py-button rounded-full border border-2px border-teal text-teal-fontcolor font-heading hover:no-underline"
            onClick={(e) => handleSmoothScroll(e, 'insurance_and_payments')}
          >
            Insurance &amp; Payment
          </a>
        )}
      </>
    );
  };

  const doctorBiography = (): ReactElement | undefined => {
    return (
      content.about && (
        <DoctorBiography
          content={content}
          officeInfo={officeInfo}
          doctorVideo={doctorVideo}
          siteData={siteData}
          preview={preview}
        />
      )
    );
  };

  const doctorLocations = (factua?: boolean): ReactElement | null => {
    return (
      (secondaryLocations.length > 0 && (
        <DoctorLocationsAccordian
          id="my_locations"
          locations={secondaryLocations}
          closedDates={closedDates}
          siteName={content.site.fields.name}
          markerIcon={markerIcon}
          factua={factua}
          providerId={content.providerId}
        />
      )) ||
      null
    );
  };

  const testimonials = (): ReactElement | undefined => {
    return (
      reviews && (
        <div className="ecp-my">
          <Testimonial
            title={`What patients say about Dr. ${content.lastName}`}
            testimonials={reviews.map(reviewToTestimoial)}
            backgroundColor="neutral"
          />
        </div>
      )
    );
  };

  const insuranceAndPayment = (): ReactElement | null => {
    return (
      (shouldShowInsuranceAndPayment && (
        <InsuranceAndPayment
          insuranceDisclaimer={insuranceDisclaimer}
          paymentsAccepted={paymentsAccepted}
          insuranceProviders={insuranceProviders}
          insuranceSlug={insuranceSlug}
          billingInquirySlug={billingInquirySlug}
        />
      )) ||
      null
    );
  };

  const frames = (): ReactElement | null => {
    return (
      (content?.frames && (
        <div>
          <ProductCarousel
            products={content?.frames?.fields?.products?.map((product) => product.fields) || []}
            cta={content?.frames?.fields?.cta?.fields}
            title={content?.frames?.fields?.title}
            description={replaceSiteNameInDocument(
              content?.frames?.fields?.description,
              siteData.siteName
            )}
          />
        </div>
      )) ||
      null
    );
  };
  const slimBanner = (): ReactElement | null => {
    return (
      (content?.slimBanner && (
        <div>
          <SlimHero
            heading={content?.slimBanner?.fields?.heading}
            body={replaceSiteNameInDocument(content?.slimBanner?.fields?.body, siteData.siteName)}
            image={content?.slimBanner?.fields?.image?.fields?.file?.url || ''}
            mobileImage={content?.slimBanner?.fields?.mobileImage?.fields?.file?.url}
            cta={content?.slimBanner?.fields?.cta}
          />
        </div>
      )) ||
      null
    );
  };

  const defaultComponentsOrder = {
    'Doctor Image and Page Summary': (
      <DoctorImageAndPageSummary
        content={content}
        officeInfo={officeInfo}
        doctorNameHeadingType={siteData.doctorNameHeadingType ?? 'h1'}
        displayAnchorLinks={displayAnchorLinks()}
        siteName={siteData.siteName}
        factua={siteData?.factua || false}
      />
    ),
    'Doctor Locations': doctorLocations(siteData?.factua || false),
    'Doctor Biography': doctorBiography(),
    'Insurance and Payment': insuranceAndPayment(),
    Frames: frames(),
    'Slim Banner': slimBanner(),
    Testimonials: testimonials(),
  };

  const componentsToDisplay =
    siteData.doctorPagesComponentOrder?.map((component) => {
      return defaultComponentsOrder[component.fields.name as keyof typeof defaultComponentsOrder];
    }) || [];

  return (
    <Layout
      siteData={siteData}
      preview={preview}
      nextSeoProps={nextSeoProps}
      currentSlug={currentSlug}
      pageDates={pageDates}
    >
      <div className="doctor-page">
        <PhysicianSchema
          domain={siteData.domain}
          currentSlug={currentSlug}
          name={fullNameAndTitles}
          npi={content.npi}
          officeInfo={officeInfo}
          secondaryLocations={secondaryLocations}
          specialties={content.specialties?.map((s) => s.fields.service).filter(Boolean)}
          reviewAvg={reviewAvg}
          reviews={reviews}
        />
        <SharedLocationDataContext.Provider
          value={{ scheduleAppointmentCtaText: scheduleApptCtaText }}
        >
          <BreadcrumbNav items={breadcrumbs} />

          {componentsToDisplay && componentsToDisplay.length > 0
            ? componentsToDisplay.map((component, index) => <div key={index}>{component}</div>)
            : Object.values(defaultComponentsOrder).map((component, index) => (
                <div key={index}>{component}</div>
              ))}
        </SharedLocationDataContext.Provider>
      </div>
    </Layout>
  );
}
